import { request } from './utils'
import { captureSentryError } from '../services/sentry'
import { getUserQuickActions } from '../firebase/quickActionUserSettings'
import { QuickActionConfig } from '../types'
import { getFirebaseServiceUsage } from '../firebase/servicesUsage'
import { getUser } from '../firebase/auth'
import { isEqual } from '../services/lodash'

let isInitial = true
let quickActionConfig: Record<string, QuickActionConfig[]> = {}

const STIGG_PREFIX = 'feature-integration-'

const REVALIDATE_TIME = 60 * 60 * 1000
let revalidateTimeoutID: ReturnType<typeof setTimeout> | null = null

export const resetQuickActionExtensionConfig = () => {
    isInitial = true
    quickActionConfig = {}
    if (revalidateTimeoutID) clearTimeout(revalidateTimeoutID)
}

export const fetchQuickActionConfig = async (isTimeoutRefetch?: boolean) => {
    if (revalidateTimeoutID) clearTimeout(revalidateTimeoutID)

    const firebaseUser = getUser()
    const isViewer = firebaseUser?.roles?.a === 10

    if (isViewer) {
        quickActionConfig = {}
        return
    }

    const enabledQuickActions: string[] = await getUserQuickActions()
    const serviceUsage = await getFirebaseServiceUsage()

    const quickActionsToGet = enabledQuickActions.filter(qa =>
        Boolean(serviceUsage[`${STIGG_PREFIX}${qa}`])
    )

    if (quickActionsToGet.length === 0) {
        quickActionConfig = {}
        return
    }

    const currentConfigKeys = quickActionConfig ? Object.keys(quickActionConfig).sort() : []

    // If we already have the config for the quick actions we need, we don't need to fetch it again
    if (
        !isTimeoutRefetch &&
        isEqual(currentConfigKeys, quickActionsToGet.sort())
    ) {
        return
    }

    try {
        quickActionConfig = await request(
            '/c/v1/quick-actions/get-ext-config',
            'POST',
            { quickActions: quickActionsToGet }
        )

        // Revalidate extension config once in a while
        revalidateTimeoutID = setTimeout(
            () => fetchQuickActionConfig(true),
            REVALIDATE_TIME
        )
    } catch (e) {
        captureSentryError(e)
        quickActionConfig = {}
    }
}

export const getQuickActionExtensionConfig = async () => {
    // If it's an initial call, we await until DB listeners are installed
    if (isInitial) {
        await fetchQuickActionConfig()
        isInitial = false
    }

    return quickActionConfig
}

export const getPageConfigs = (href: string) =>
    new Promise<QuickActionConfig[]>(resolve => {
        if (!href || !quickActionConfig) {
            resolve([])
            return
        }

        const relevantConfigs: QuickActionConfig[] = Object.entries(
            quickActionConfig
        ).flatMap(([value, configs]) =>
            configs
                .filter(config => href.includes(config.url))
                .map(config => ({ ...config, value }))
        )

        resolve(relevantConfigs)
    })
